import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { Calendar, LogOut, Plus, Users, Monitor, ExternalLink, Settings, List, Mail, UserCog, ShieldAlert } from 'lucide-react';
import RoomCard from '@/components/RoomCard';
import BookingForm from '@/components/BookingForm';
import BookingsList from '@/components/BookingsList';
import { useNavigate } from 'react-router-dom';
import { useBranding } from '@/context/BrandingContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Dashboard() {
  const { user, logout } = useAuth();
  const { branding, getLogoUrl } = useBranding();
  const navigate = useNavigate();
  const [rooms, setRooms] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [showBookingForm, setShowBookingForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [roomsRes, bookingsRes] = await Promise.all([
        axios.get(`${API}/rooms`),
        axios.get(`${API}/bookings`)
      ]);
      setRooms(roomsRes.data);
      setBookings(bookingsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleBookRoom = (room) => {
    setSelectedRoom(room);
    setShowBookingForm(true);
  };

  const handleBookingCreated = () => {
    setShowBookingForm(false);
    setSelectedRoom(null);
    fetchData();
  };

  const handleCancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success('Reserva cancelada com sucesso');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao cancelar reserva');
    }
  };

  const openRoomDisplay = (roomId) => {
    window.open(`/sala/${roomId}`, '_blank');
  };

  const logoUrl = getLogoUrl();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/40 bg-white/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center overflow-hidden">
              {logoUrl ? (
                <img src={logoUrl} alt={branding.system_name} className="w-full h-full object-contain bg-white" />
              ) : (
                <Calendar className="w-5 h-5 text-primary-foreground" />
              )}
            </div>
            <h1 className="text-xl font-manrope font-bold tracking-tight">{branding.system_name}</h1>
          </div>
          <div className="flex items-center gap-4">
            {user?.is_admin && (
              <Button
                data-testid="email-approvals-button"
                onClick={() => navigate('/admin/aprovacoes-email')}
                variant="ghost"
                size="sm"
                className="rounded-full"
              >
                <ShieldAlert className="w-4 h-4 mr-2" />
                Aprovações
              </Button>
            )}
            {user?.is_admin && (
              <Button
                data-testid="branding-admin-button"
                onClick={() => navigate('/admin/branding')}
                variant="ghost"
                size="sm"
                className="rounded-full"
              >
                <Settings className="w-4 h-4 mr-2" />
                Branding
              </Button>
            )}
            {user?.is_admin && (
              <Button
                data-testid="users-admin-button"
                onClick={() => navigate('/admin/usuarios')}
                variant="ghost"
                size="sm"
                className="rounded-full"
              >
                <UserCog className="w-4 h-4 mr-2" />
                Usuarios
              </Button>
            )}
            <Button
              data-testid="reunioes-admin-button"
              onClick={() => navigate('/admin/reunioes')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <List className="w-4 h-4 mr-2" />
              Reuniões
            </Button>
            <Button
              data-testid="email-settings-button"
              onClick={() => navigate('/configuracoes/email')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <Mail className="w-4 h-4 mr-2" />
              Email
            </Button>
            <Button
              data-testid="settings-button"
              onClick={() => navigate('/configuracoes/salas')}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <Settings className="w-4 h-4 mr-2" />
              Configurações
            </Button>
            <div className="text-right hidden sm:block">
              <p className="text-sm font-medium font-inter">{user?.name}</p>
              <p className="text-xs text-muted-foreground">{user?.email}</p>
            </div>
            <Button
              data-testid="logout-button"
              onClick={logout}
              variant="ghost"
              size="sm"
              className="rounded-full"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {/* Welcome Section */}
        <div className="mb-12">
          <h2 className="text-4xl md:text-5xl font-manrope font-bold tracking-tight mb-3">
            Olá, {user?.name?.split(' ')[0]} 👋
          </h2>
          <p className="text-lg text-muted-foreground font-inter">
            Gerencie suas reservas de salas de reunião
          </p>
        </div>

        {/* Rooms Section */}
        <section className="mb-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h3 className="text-2xl font-manrope font-bold tracking-tight mb-1">Salas Disponíveis</h3>
              <p className="text-muted-foreground font-inter">Selecione uma sala para fazer uma reserva</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {rooms.map((room, index) => (
              <div key={room.id} className="relative">
                <RoomCard
                  room={room}
                  onBook={handleBookRoom}
                  bookings={bookings.filter(b => b.room_id === room.id)}
                  imageIndex={index}
                />
                <Button
                  onClick={() => openRoomDisplay(room.id)}
                  className="absolute top-4 right-4 bg-white/90 hover:bg-white text-primary rounded-full shadow-lg backdrop-blur-sm"
                  size="sm"
                >
                  <Monitor className="w-4 h-4 mr-2" />
                  Ver Display
                  <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </div>
            ))}
          </div>
        </section>

        {/* Bookings List */}
        <BookingsList
          bookings={bookings}
          rooms={rooms}
          currentUserId={user?.id}
          onCancel={handleCancelBooking}
        />
      </main>

      {/* Booking Form Dialog */}
      {showBookingForm && selectedRoom && (
        <BookingForm
          room={selectedRoom}
          onClose={() => {
            setShowBookingForm(false);
            setSelectedRoom(null);
          }}
          onSuccess={handleBookingCreated}
          existingBookings={bookings.filter(b => b.room_id === selectedRoom.id)}
        />
      )}
    </div>
  );
}
